/**
 * Painel de critérios analisados — refatorado para 10 critérios (E10.F7). // ux-neutrality:allow doc-comment
 *
 * Renderiza um card por critério presente em `criteria_breakdown` (1..10), // ux-neutrality:allow doc-comment
 * colapsado por padrão. Click expande para mostrar evidência textual + // ux-neutrality:allow doc-comment
 * botão "Perguntar ao Agente". Cada card mostra: // ux-neutrality:allow doc-comment
 *  - nome PT-BR via `criterio.nome.X` (i18n) // ux-neutrality:allow doc-comment
 *  - LR numérico // ux-neutrality:allow doc-comment
 *  - badge de status vindo do backend (`criteria_status`, banda canônica // ux-neutrality:allow doc-comment
 *    [1/3, 3] — E25.F3/ADR-0035) — cores via CSS .criteria-badge--{status}. // ux-neutrality:allow doc-comment
 *
 * Dívida: bbox highlighting na contestada (T02) requer expor // ux-neutrality:allow doc-comment
 * `evidence_coords` no `CaseAnalysisData` — não fizemos em F7 pra não // ux-neutrality:allow doc-comment
 * alargar escopo; registrada como pendência para Fase futura. // ux-neutrality:allow doc-comment
 */
import { useState } from 'react';
import { Blueprint } from '@/components/ui/Blueprint';
import { MonoData } from '@/components/ui/MonoData';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseAnalysisData, LrAdjustmentStateData } from '@/lib/case_workspace';
import {
  criterionDisplayName,
  criterionStatusFrom,
  type DerivedStatus,
} from '@/lib/criterion_status';
import { LrAdjustControl } from './LrAdjustControl';

export interface CriteriaPanelProps {
  analysis: CaseAnalysisData;
  onAskAgent: (criterionId: string) => void;
  // E33.F2/ADR-0044: edição do LR por critério na mesa. Opcionais — quando
  // ausentes (ex.: testes legados), o LR é exibido somente-leitura.
  adjustment?: LrAdjustmentStateData | null;
  onAdjustCriterion?: (criterionKey: string, value: number) => void | Promise<void>;
  onClearCriterion?: (criterionKey: string) => void | Promise<void>;
  adjusting?: boolean;
}

// (nome PT-BR do critério resolvido por `criterionDisplayName` — single-source
// em `@/lib/criterion_status`, reusado pela marcação na questionada.)
// Mapeia chave do `criteria_breakdown` (snake_case do backend) para a chave
// do dicionário i18n `criterio.nome.*`. Mantém divergência histórica: o
// frontend chama o critério 2 de `inclinacao` (vs `inclinacao_axial` no JSON
// calibrado). Documentado em case_workspace_service.CRITERION_KEYS.
function findEvidence(analysis: CaseAnalysisData, key: string): string | null {
  for (const p of analysis.patterns) {
    if (p.criteria_evidence && key in p.criteria_evidence) {
      return p.criteria_evidence[key] ?? null;
    }
  }
  return null;
}

interface CriterionBodyProps {
  criterionKey: string;
  evidence: string | null;
  onAskAgent: (criterionId: string) => void;
  noSignal: boolean;
}

function CriterionBody({
  criterionKey,
  evidence,
  onAskAgent,
  noSignal,
}: CriterionBodyProps): JSX.Element {
  if (noSignal) {
    return (
      <div className="criteria-body" data-testid={`criteria-body-${criterionKey}`}>
        <p className="criteria-evidence criteria-evidence--empty">
          {ptBR.workspaceCaso.criteriaNoSignalHint}
        </p>
      </div>
    );
  }
  return (
    <div className="criteria-body" data-testid={`criteria-body-${criterionKey}`}>
      {evidence ? (
        <p className="criteria-evidence">{evidence}</p>
      ) : (
        <p className="criteria-evidence criteria-evidence--empty">
          {ptBR.workspaceCaso.criteriaEvidenceMissing}
        </p>
      )}
      <button
        type="button"
        className="criteria-ask"
        onClick={() => onAskAgent(criterionKey)}
        data-testid={`criteria-ask-${criterionKey}`}
      >
        {ptBR.workspaceCaso.criteriaAskAgent}
      </button>
    </div>
  );
}

interface CriterionCardProps {
  criterionKey: string;
  lr: number;
  /** E25.F3/ADR-0035: status resolvido pelo backend (banda canônica). */
  bandStatus: DerivedStatus;
  evidence: string | null;
  onAskAgent: (criterionId: string) => void;
  /** E23.F5/ADR-0023: critério sem sinal extraível — rótulo honesto, sem LR. */
  noSignal?: boolean;
  // E33.F2/ADR-0044: edição do LR do critério. `adjusted` mostra o selo +
  // reverter; opcionais (sem handlers = modo só-leitura).
  adjusted?: boolean;
  busy?: boolean;
  onAdjust?: ((criterionKey: string, value: number) => void | Promise<void>) | undefined;
  onClear?: ((criterionKey: string) => void | Promise<void>) | undefined;
}

/** Slot do LR à direita do critério: input editável (E33.F2) ou só-leitura. */
function CriterionLrSlot({
  criterionKey,
  name,
  lr,
  adjusted,
  busy,
  onAdjust,
  onClear,
}: {
  criterionKey: string;
  name: string;
  lr: number;
  adjusted: boolean;
  busy: boolean;
  onAdjust?: ((criterionKey: string, value: number) => void | Promise<void>) | undefined;
  onClear?: ((criterionKey: string) => void | Promise<void>) | undefined;
}): JSX.Element {
  if (!onAdjust || !onClear) {
    return <span className="criteria-lr">LR = {lr.toPrecision(3)}</span>;
  }
  return (
    <LrAdjustControl
      effective={lr}
      adjusted={adjusted}
      busy={busy}
      testIdPrefix={`criterion-${criterionKey}`}
      inputAriaLabel={`${ptBR.workspaceCaso.lrAdjustInputLabel} — ${name}`}
      onCommit={(value) => onAdjust(criterionKey, value)}
      onClear={() => onClear(criterionKey)}
    />
  );
}

/** Botão de expandir/recolher (chevron) — extraído para enxugar CriterionCard. */
function CriterionChevron({
  criterionKey,
  expanded,
  onToggle,
}: {
  criterionKey: string;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="criteria-chevron-btn"
      aria-expanded={expanded}
      aria-label={
        expanded ? ptBR.workspaceCaso.criteriaCollapse : ptBR.workspaceCaso.criteriaExpand
      }
      onClick={onToggle}
      data-testid={`criteria-chevron-${criterionKey}`}
    >
      <span className="criteria-chevron" aria-hidden="true">
        {expanded ? '−' : '+'}
      </span>
    </button>
  );
}

function CriterionCard({
  criterionKey,
  lr,
  bandStatus,
  evidence,
  onAskAgent,
  noSignal = false,
  adjusted = false,
  busy = false,
  onAdjust,
  onClear,
}: CriterionCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Sem sinal usa o visual neutro (cinza), mas com rótulo "Não avaliado" — o LR
  // (=1,0) não é exibido, pois não houve avaliação a reportar.
  const status: DerivedStatus = noSignal ? 'neutral' : bandStatus;
  const name = criterionDisplayName(criterionKey);
  const statusLabel = noSignal
    ? ptBR.workspaceCaso.criteriaNoSignalBadge
    : ptBR.criterio.status[status];
  return (
    <li
      className={[
        'criteria-row',
        `criteria-row--${status}`,
        noSignal ? 'criteria-row--no-signal' : '',
        expanded ? 'criteria-row--expanded' : '',
        adjusted ? 'criteria-row--adjusted' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`criteria-row-${criterionKey}`}
      data-criterion-signal={noSignal ? 'no_signal' : 'evaluated'}
    >
      {/* O input editável não pode aninhar dentro do botão de expandir (HTML
          inválido), então a linha é um flex: botão (nome + badge) · LR · chevron.
          Ordem do LR ANTES do chevron preserva o visual antigo ("Neutra · LR ·"). */}
      <div className="criteria-header">
        <button
          type="button"
          className="criteria-header-main"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          data-testid={`criteria-toggle-${criterionKey}`}
        >
          <span className="criteria-name">{name}</span>
          <span
            className={`criteria-badge criteria-badge--${status}`}
            data-testid={`criteria-badge-${criterionKey}`}
          >
            {statusLabel}
          </span>
        </button>
        {!noSignal && (
          <CriterionLrSlot
            criterionKey={criterionKey}
            name={name}
            lr={lr}
            adjusted={adjusted}
            busy={busy}
            onAdjust={onAdjust}
            onClear={onClear}
          />
        )}
        <CriterionChevron
          criterionKey={criterionKey}
          expanded={expanded}
          onToggle={() => setExpanded((current) => !current)}
        />
      </div>
      {expanded && (
        <CriterionBody
          criterionKey={criterionKey}
          evidence={evidence}
          onAskAgent={onAskAgent}
          noSignal={noSignal}
        />
      )}
    </li>
  );
}

function isNoSignal(analysis: CaseAnalysisData, key: string): boolean {
  return analysis.criteria_signal?.[key] === 'no_signal';
}

export function CriteriaPanel({
  analysis,
  onAskAgent,
  adjustment = null,
  onAdjustCriterion,
  onClearCriterion,
  adjusting = false,
}: CriteriaPanelProps): JSX.Element {
  const [showNoSignal, setShowNoSignal] = useState(false);
  const entries = Object.entries(analysis.criteria_breakdown ?? {});
  // E23.F5/ADR-0023: critérios "sem sinal" (4 dinâmicos cegos em foto de papel)
  // ficam ocultos por padrão — o perito vê só o que foi de fato avaliado, com
  // toggle para auditar os não avaliados (que NÃO somem do registro).
  const evaluated = entries.filter(([key]) => !isNoSignal(analysis, key));
  const noSignal = entries.filter(([key]) => isNoSignal(analysis, key));
  const adjustedCriteria = adjustment?.adjusted_criteria ?? {};
  return (
    <Blueprint
      as="section"
      className="criteria-panel"
      aria-label={ptBR.workspaceCaso.criteriaPanelTitle}
    >
      {/* Prancha 2B: título com a contagem do que foi de fato avaliado logo
          abaixo, em mono — o perito lê "6 de 10" antes de ler a lista. */}
      <header>
        <h3>{ptBR.workspaceCaso.criteriaPanelTitle}</h3>
        <MonoData variant="label" className="mt-1 block" data-testid="criteria-evaluated-count">
          {ptBR.workspaceCaso.criteriaEvaluatedCount
            .replace('{evaluated}', String(evaluated.length))
            .replace('{total}', String(entries.length))}
        </MonoData>
      </header>
      <ul>
        {evaluated.map(([key, lr]) => (
          <CriterionCard
            key={key}
            criterionKey={key}
            lr={lr}
            bandStatus={criterionStatusFrom(analysis.criteria_status, key)}
            evidence={findEvidence(analysis, key)}
            onAskAgent={onAskAgent}
            adjusted={key in adjustedCriteria}
            busy={adjusting}
            onAdjust={onAdjustCriterion}
            onClear={onClearCriterion}
          />
        ))}
      </ul>
      {noSignal.length > 0 && (
        <div className="criteria-no-signal">
          <button
            type="button"
            className="criteria-no-signal-toggle"
            aria-expanded={showNoSignal}
            onClick={() => setShowNoSignal((current) => !current)}
            data-testid="criteria-no-signal-toggle"
          >
            {showNoSignal
              ? ptBR.workspaceCaso.criteriaHideNoSignal
              : `${ptBR.workspaceCaso.criteriaShowNoSignal} (${noSignal.length})`}
          </button>
          {showNoSignal && (
            <ul data-testid="criteria-no-signal-list">
              {noSignal.map(([key, lr]) => (
                <CriterionCard
                  key={key}
                  criterionKey={key}
                  lr={lr}
                  bandStatus={criterionStatusFrom(analysis.criteria_status, key)}
                  evidence={findEvidence(analysis, key)}
                  onAskAgent={onAskAgent}
                  noSignal
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </Blueprint>
  );
}
